/**
 * **回合队列的槽位算术** —— 谁站在第几行、站在那儿长什么样。
 *
 * 评审把这一列的做法改掉了：
 *
 * > 回合队列的实现方式不对，固定显示偏移 0（当前玩家），偏移 -1，-2，-3，
 * > +1，+2，+3 以此类推。轮到下一个玩家时，将所有文本的样式和位置与下一个
 * > 进行补间动画。
 *
 * ## 和上一版的区别
 *
 * 上一版是"**轮转 + 整列向上滚一行**"：顺序每次轮转一格、`#offset` 从 0 动到
 * `-rowHeight`，滚完再轮转回来。它的动作是"**一列一起往上走**"——
 * 每个人的落点都是"上一个人刚才待的地方"，滚动本身不表达"谁轮到谁"。
 *
 * 现在**不轮转**：位置是**每个人的回合距离**换算出来的一个固定偏移，
 * 行动中那个人 0、他后面的人 +1/+2/…、已经打过的人 -1/-2/…。
 * 换人时每个人的偏移都会变（一般全列各挪一格），于是
 * **所有人的位置和样式一起补间到新槽位** —— 一次真正的"队列重排"。
 *
 * ## 为什么偏移要折成"最短带符号距离"
 *
 * 回合是**循环**的，所以"第几个轮到他"只有 0..n-1 这一种说法；
 * 显示成 -/+ 只是把它摊在行动者的上下两边（打过的人在上面、
 * 还没打的人在下面）。取**最短**的那一侧，是为了 5 个人时正好占 ±2 行 ——
 * 一整列不用滚动就能全看见（这个游戏 2–5 人，见 `core/config.ts`）。
 *
 * ⚠️ 正对面那一个（偶数人时的 d = n/2）两边一样近，**算下面**：
 * "还没打的人在下半边"这条要一致，不能看运气。
 *
 * ## 这一层为什么不 import Pixi
 *
 * 槽位是**纯算术**（`displayOffsets` / `slotStyle`），而它正是"看着对、
 * 其实错了也看不出来"的那部分（谁在上谁在下、平局往哪边倒、颜色怎么排）。
 * 所以它和 `render/ui/ink.ts` 一样单独放一个文件，好在 node 里逐条钉住；
 * 摆放和补间在 `turnQueue.ts`。
 */

import { INK } from '../ui/ink';

/** 槽位样式的常数。散在算式里的话，"调一下距离感"就变成一次考古 */
export const SLOT = {
  /** 离行动中那一行每远一行，尺寸缩多少 */
  SIZE_STEP: 0.12,
  /** 最远的那一行最小缩到多少（再小就认不出名字了） */
  MIN_SCALE: 0.66,
  /** 离行动中那一行每远一行，透明度降多少 */
  ALPHA_STEP: 0.26,
  /** 最远的那一行最淡到多少 */
  MIN_ALPHA: 0.24,
} as const;

/** 一个槽位长什么样。**补间的两端都是它** */
export interface SlotStyle {
  /** 相对"行动中那一行"的缩放 */
  readonly scale: number;
  readonly alpha: number;
  /** 字重：0 = 普通，1 = 粗体。两层文字交叉淡入淡出，见 `turnQueue.ts` */
  readonly weight: number;
  /** 文字颜色（画的时候用 `tint`，所以颜色本身也能补间） */
  readonly tint: number;
  /** "点它"那个记号的不透明度。只有能点的行才是 1 */
  readonly mark: number;
}

/**
 * **每个座位相对行动者的显示偏移。** 返回的顺序和传进来的座位顺序一一对应。
 *
 * 负数在上（这一轮已经打过了），正数在下（还没轮到）。`activeIndex` 越界时
 * 当成第 0 个 —— 界面不该因为一个不认识的 id 就崩。
 */
export function displayOffsets(count: number, activeIndex: number): number[] {
  const n = Math.max(0, Math.floor(count));
  const active = activeIndex >= 0 && activeIndex < n ? activeIndex : 0;
  const out: number[] = [];
  for (let seat = 0; seat < n; seat++) {
    // 按**回合推进方向**数的距离：0 = 他自己，1 = 下一个，…，n-1 = 上一个
    const distance = (seat - active + n) % n;
    // 折到最短的那一侧。⚠️ 平局（偶数人的正对面）算正数，见文件头
    out.push(distance * 2 <= n ? distance : distance - n);
  }
  return out;
}

/**
 * 一个偏移槽位的样式。
 *
 * - `alive` 只影响颜色：出局的人连"曾经轮到过他"的那点酸色也不给
 * - `target` 是"这一行现在能不能点"（见 `TurnQueue.setTargets`），
 *   它只影响最后那个记号 —— 所以它也是补间的一部分，不是开关
 */
export function slotStyle(offset: number, alive: boolean, target = false): SlotStyle {
  const away = Math.abs(offset);
  return {
    scale: 1 - Math.min(1 - SLOT.MIN_SCALE, SLOT.SIZE_STEP * away),
    alpha: away === 0 ? 1 : Math.max(SLOT.MIN_ALPHA, 1 - SLOT.ALPHA_STEP * away),
    weight: away === 0 ? 1 : 0,
    // 行动中那张脸是酸的（`INK.accent`）。出局的人连这一点都不给
    tint: !alive ? INK.textDim : away === 0 ? INK.accent : INK.text,
    mark: target ? 1 : 0,
  };
}
